/**
 * The client end of the ghost relay.
 *
 * Everything here is optional to the game. The socket may fail to open, drop, or never receive
 * anything at all, and none of that reaches the player as an error — the band simply stays empty,
 * which is exactly what it does when nobody else is online. Ghosts are enrichment; the solo story
 * cannot depend on them and does not.
 *
 * That is why there is no reconnect storm, no "connecting…" indicator and no failure toast. A
 * player who can never reach the relay should be unable to tell that the relay exists.
 */
import type { GhostMessage, ServerMessage } from './protocol.js';

export interface GhostRelayOptions {
  /** Called with each fragment to play. */
  onGhost: (ghost: GhostMessage) => void;
  /** Called when the server says whether this account may transmit. */
  onStatus?: (canTransmit: boolean) => void;
}

/** Nobody knows: the relay has not said, or is not there. */
const UNKNOWN_OTHERS = -1;

/** Reconnect backoff. Slow on purpose — nothing is waiting on this. */
const RECONNECT_BASE_MS = 4_000;
const RECONNECT_MAX_MS = 120_000;

export class GhostRelay {
  private socket: WebSocket | null = null;
  private reconnectAttempt = 0;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private closed = false;
  private khz: number | null = null;
  private others = UNKNOWN_OTHERS;
  private readonly options: GhostRelayOptions;

  constructor(options: GhostRelayOptions) {
    this.options = options;
  }

  get isConnected(): boolean {
    // `this.socket.OPEN` rather than the `WebSocket` global: on a browser without WebSocket at
    // all, touching the global throws — and this getter exists precisely to be safe there.
    return this.socket !== null && this.socket.readyState === this.socket.OPEN;
  }

  connect(): void {
    if (this.closed || this.socket) return;

    let socket: WebSocket;
    try {
      const url = new URL('/api/relay', window.location.href);
      url.protocol = url.protocol === 'https:' ? 'wss:' : 'ws:';
      socket = new WebSocket(url);
    } catch {
      // No WebSocket, a blocked URL, anything: the band is just empty.
      return;
    }

    this.socket = socket;

    socket.addEventListener('open', () => {
      this.reconnectAttempt = 0;
      // Tell the relay where we are listening, if the dial already moved.
      if (this.khz !== null) this.tune(this.khz);
    });

    socket.addEventListener('message', (event: MessageEvent<string>) => {
      let message: ServerMessage;
      try {
        message = JSON.parse(event.data) as ServerMessage;
      } catch {
        return;
      }
      if (message.type === 'ghost') this.options.onGhost(message);
      else if (message.type === 'status') {
        if (typeof message.others === 'number') this.others = message.others;
        this.options.onStatus?.(message.canTransmit);
      }
    });

    const drop = (): void => {
      if (this.socket !== socket) return;
      this.socket = null;
      this.scheduleReconnect();
    };
    socket.addEventListener('close', drop);
    socket.addEventListener('error', drop);
  }

  /**
   * Reconnect with a long backoff and no ceiling on patience.
   *
   * Deliberately unhurried: a relay outage should cost nothing and be invisible. A tight retry
   * loop would turn a server problem into every client hammering it, for a feature nobody is
   * waiting on.
   */
  private scheduleReconnect(): void {
    if (this.closed || this.reconnectTimer !== null) return;
    const delay = Math.min(RECONNECT_BASE_MS * 2 ** this.reconnectAttempt, RECONNECT_MAX_MS);
    this.reconnectAttempt += 1;
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      this.connect();
    }, delay);
  }

  /**
   * Roughly how busy the current band is, for Chapter 6's coverage NPCs.
   *
   * `others: -1` means the relay has not said — no socket, no answer yet, or the dial has moved
   * somewhere the server has not reported on. Coverage treats unknown as empty, which is the
   * right way round: with no relay the player really is alone as far as the game can tell, and
   * that is exactly the case a coverage NPC exists for.
   */
  population(): { others: number } {
    return { others: this.isConnected ? this.others : UNKNOWN_OTHERS };
  }

  /** Tell the relay which frequency the player is listening on. */
  tune(khz: number): void {
    // The old band's figure says nothing about the new one, so it is discarded rather than left
    // to stand until the server answers — a stale count is worse than admitting ignorance.
    if (khz !== this.khz) this.others = UNKNOWN_OTHERS;
    this.khz = khz;
    if (!this.isConnected) return;
    this.socket!.send(JSON.stringify({ type: 'tune', khz }));
  }

  /**
   * Offer a fragment of the player's voice.
   *
   * `sttText` is sent for moderation only. It is what the recogniser heard, not a recording, and
   * the server discards it after screening — but a player should know it leaves the machine, so
   * the privacy policy covers it and the consent gate is what makes this lawful.
   */
  offer(fragment: {
    khz: number;
    durationMs: number;
    mime: string;
    audio: string;
    sttText?: string;
  }): void {
    if (!this.isConnected) return;
    this.socket!.send(JSON.stringify({ type: 'offer', ...fragment }));
  }

  close(): void {
    this.closed = true;
    if (this.reconnectTimer !== null) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    this.socket?.close();
    this.socket = null;
  }
}
